<template>
    <div :class="mainClass">
        <page-header
            v-if="includeHeader"
            :title="title"
            :breadcrumbs="breadcrumbs"
            :back-route="backRoute"
        />

        <div v-if="!editMode || (editMode && item.id)" class="row">
            <div class="col-xl-9 mb-3" :class="dynamicClass">
                <div v-if="languagePicker" class="card mb-3">
                    <div class="card-body">
                        <language-chooser
                            v-model="language"
                            :has-error="$v.language.$error"
                        />
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            ref="addEditForm"
                            v-model="item"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @on-name-change="onNameChange"
                        />
                    </div>
                </div>
                <div class="my-3">
                    <component
                        :is="componentItem.name"
                        v-for="componentItem of additionalFormComponents"
                        :key="componentItem.name"
                        v-bind="componentItem.props || {}"
                        v-on="componentItem.events || {}"
                    />
                </div>
                <div v-if="meta" class="card mb-3">
                    <div class="card-body">
                        <meta-data
                            :meta-data="item.meta"
                            @meta-change="onMetaChange"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <a
                                v-if="preview && editMode"
                                :href="previewUrl"
                                class="btn btn-primary btn-static waves-effect waves-light m-1"
                                target="_blank"
                                @click="onPreviewButtonClick"
                            >
                                <i class="far fa-eye" />
                                Preview
                            </a>
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                            <!-- // @NOTE consider button that would give link with resource id (could be used instead of slug lnik that might change and break other places) -->
                        </div>

                        <item-information
                            :key="informationKey"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="item"
                            :slug="slug"
                            :options="itemInformationOptions"
                            :featured-image="featuredImage"
                            :get-categories-function-name="
                                getCategoriesFunctionName
                            "
                            :categories="categories"
                            :get-medication-categories-function-name="
                                getMedicationCategoriesFunctionName
                            "
                            :medication-categories="medicationCategories"
                            :get-branches-function-name="
                                getBranchesFunctionName
                            "
                            :branch="branch"
                            :slug-error="slugError"
                            :contents="contents"
                            :get-contents-function-name="
                                getContentsFunctionName
                            "
                            @on-slug-change="onSlugChange"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-branch-change="onBranchChange"
                            @on-categories-change="onCategoriesChange"
                            @on-medication-categories-change="
                                onMedicationCategoriesChange
                            "
                            @on-featured-image-change="onFeaturedImageChange"
                            @on-contents-change="onContentsChange"
                        />
                    </div>
                </div>
            </div>

            <component
                :is="componentItem.name"
                v-for="componentItem of additionalComponents"
                :key="componentItem.name"
                v-bind="componentItem.props || {}"
                v-on="componentItem.events || {}"
            />
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';

import { frontendUrl } from '@/config';
import languageToSlug from '@/helpers/languageToSlug';
import MetaData from '@/components/elements/MetaData';
import LanguageChooser from '@/components/elements/LanguageChooser';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    validations: {
        item: {}
    },

    components: {
        MetaData,
        LanguageChooser,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            mainClass: 'single-view',
            item: {
                meta: {}
            },
            language: { name: '' },
            id,
            preview: true,
            editMode: !!id,
            informationKey: 0,
            serverErrors: [],
            itemValueStringify: '',
            includeHeader: true,
            backRoute: '/items',
            indexName: 'Items',
            singleItemName: 'item',
            getCategoriesFunctionName: '',
            getMedicationCategoriesFunctionName: '',
            getBranchesFunctionName: '',
            getContentsFunctionName: '',
            languagePicker: false,
            meta: false
        };
    },

    computed: {
        ...mapGetters({
            selectedLanguage: 'languages/selectedLanguage'
        }),

        dynamicClass() {
            return this.language.code === 'he' ? 'rtl' : '';
        },

        title() {
            return this.id
                ? `Edit ${this.singleItemName}`
                : `Add ${this.singleItemName}`;
        },

        breadcrumbs() {
            return [
                {
                    text: 'Cancer',
                    to: '/'
                },
                {
                    text: this.indexName,
                    to: this.backRoute
                },
                {
                    text: this.id ? 'Edit' : 'Add',
                    active: true
                }
            ];
        },

        sections() {
            const sections = {
                permalink: true,
                meta: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        previewUrl() {
            const { language, slug } = this.item;

            const languageSlug = languageToSlug(language);

            const url = frontendUrl.concat(languageSlug);

            return url.concat(`${this.backRoute}/${slug}`);
        },

        slug() {
            return this.item.slug;
        },

        featuredImage() {
            return this.item.featuredImageId;
        },

        categories() {
            return this.item.categories;
        },

        medicationCategories() {
            return this.item.medicationCategories;
        },

        branch() {
            return this.item.branch;
        },

        itemInformationOptions() {
            return {};
        },

        additionalComponents() {
            return [];
        },

        additionalFormComponents() {
            return [];
        },

        additionalStoreData() {
            return {};
        },

        slugError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.item,
                this.serverErrors,
                'slug'
            );
        },

        contents() {
            return this.item.contents;
        }
    },

    watch: {
        selectedLanguage: {
            handler() {
                this.language = this.selectedLanguage;
            },
            deep: true
        },
        language: {
            handler() {
                this.item.language = this.language;
            },
            deep: true
        }
    },

    async created() {
        if (this.selectedLanguage.code) {
            this.language = this.selectedLanguage;
        }

        if (this.editMode) {
            try {
                this.item = await this.getItem(this.id);

                this.itemValueStringify = JSON.stringify(this.item);

                if (this.languagePicker) {
                    this.language = this.item.language;
                }
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        getItem() {
            return {};
        },
        deleteItem() {},
        addItem() {},
        updateItem() {},
        constructStoreActionBackRoute(data) {
            return `${this.backRoute}/${data.id}/edit`;
        },

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const data = {
                ...this.item,
                ...this.additionalStoreData
            };

            if (this.languagePicker) {
                data.languageId = this.language.id;
            }

            try {
                if (!this.editMode) {
                    const added = await this.addItem(data);

                    this.$router.push(
                        this.constructStoreActionBackRoute(added)
                    );
                } else {
                    const { updatedAt } = await this.updateItem({
                        id: this.id,
                        data
                    });

                    this.item.updatedAt = updatedAt;
                    this.item.language = this.language;
                    this.itemValueStringify = JSON.stringify(this.item);

                    this.$toaster('Item updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteItem(this.id);

                this.$router.push(this.backRoute);
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        onPreviewButtonClick(e) {
            if (this.itemValueStringify !== JSON.stringify(this.item)) {
                e.preventDefault();

                this.$toaster('Please save changes before preview!', {
                    title: 'Warning!',
                    variant: 'warning'
                });
            }
        },

        onMetaChange(meta) {
            this.item.meta = { ...meta };
        },

        onNameChange(name) {
            if (this.editMode) {
                return;
            }

            if (!this.excludeSlug) {
                this.onSlugChange(name);
                this.informationKey++;
            }
        },

        onSlugChange(slug) {
            this.item.slug = slug;
        },

        onFeaturedImageChange(imageId) {
            this.item.featuredImageId = imageId;
        },

        onCategoriesChange(categories) {
            this.item.categories = categories;
            this.informationKey++;
        },

        onMedicationCategoriesChange(medicationCategories) {
            this.item.medicationCategories = medicationCategories;
            this.informationKey++;
        },

        onBranchChange(branch) {
            this.item.branch = branch;
            this.informationKey++;
        },

        onContentsChange(contents) {
            this.item.contents = contents;
            this.informationKey++;
        }
    }
};
</script>
